// Thin typed fetch wrapper against the backend's REST API (see
// backend/src/capability_registry/handler.py for the exact route list this mirrors).
// Every call needs a bearer token except health — the backend's HTTP API sits behind
// an Auth0 JWT authorizer for everything else (see backend/infra/template.yaml).

/** A non-2xx response, carrying every message the backend gave (not just the first), so
 * pages can show the full list — e.g. each junk-check reason on a 422 from `POST /maps`,
 * or the 409 a stale `base_updated_at` earns. `status` lets callers branch on the code. */
export class ApiError extends Error {
  status: number;
  errors: string[];

  constructor(status: number, errors: string[]) {
    super(errors[0] ?? `Request failed with ${status}`);
    this.status = status;
    this.errors = errors;
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8765";

export type TokenGetter = () => Promise<string | null>;

let getToken: TokenGetter = async () => null;

/** Wires the Auth0 access-token getter in once, at app startup (see
 * src/auth/ApiTokenBridge.tsx) — every call below goes through this, so nothing else
 * in the app needs to know how the token is obtained. */
export function configureApiAuth(getter: TokenGetter): void {
  getToken = getter;
}

/** Every API call funnels through here: attaches the bearer token when there is one,
 * parses JSON (or falls back to text), and turns any non-2xx into an ApiError. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (token) headers.authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) throw new ApiError(res.status, errorMessages(res.status, body));
  return body as T;
}

/** The backend returns `{errors: [...]}`; API Gateway itself (e.g. its JWT authorizer's
 * 401) returns `{message: "..."}`. Anything else falls back to the status code rather
 * than stringifying an object into "[object Object]". */
function errorMessages(status: number, body: unknown): string[] {
  if (body && typeof body === "object") {
    if ("errors" in body && Array.isArray(body.errors)) return body.errors as string[];
    if ("message" in body && typeof body.message === "string") return [`${body.message} (${status})`];
    return [`Request failed with ${status}`];
  }
  return [body ? String(body) : `Request failed with ${status}`];
}

function get<T>(path: string): Promise<T> {
  return request<T>(path, { method: "GET" });
}

// The catalog lists (tools, standards, capabilities, practices, guardrails) rarely change, so
// a list fetched once is kept here for the tab: moving between pages shows it at once, while
// a background request refreshes it for next time. That request is cheap — the backend sends
// an ETag, so the browser re-checks it and gets a 304 when nothing changed. Any change made
// here (a POST or PATCH) clears the lot, so a page reloaded after an edit always gets fresh
// data.
const memory = new Map<string, unknown>();
// Bumped by every change, so a refresh that started before one doesn't store what it fetched.
let generation = 0;

/** A GET served from the in-memory cache when it has the path (refreshing it behind the
 * scenes), otherwise fetched and kept. */
function getCached<T>(path: string): Promise<T> {
  const startedIn = generation;
  const fetchAndKeep = () =>
    request<T>(path, { method: "GET" }).then((value) => {
      if (generation === startedIn) memory.set(path, value);
      return value;
    });
  if (memory.has(path)) {
    void fetchAndKeep().catch(() => memory.delete(path));
    return Promise.resolve(memory.get(path) as T);
  }
  return fetchAndKeep();
}

/** A change: whatever it is, cached lists may now be out of date. */
async function mutate<T>(path: string, init: RequestInit): Promise<T> {
  try {
    return await request<T>(path, init);
  } finally {
    generation += 1;
    memory.clear();
  }
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return mutate<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) });
}

function patch<T>(path: string, body: unknown): Promise<T> {
  return mutate<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}

/** The only HTTP surface the rest of the app uses — src/api/endpoints.ts wraps it per
 * route. `getCached` is for the catalog lists only. There's no plain-link URL: every call needs
 * the sign-in token, which a browser navigation wouldn't send. */
export const api = { get, getCached, post, patch };
